/**
 * Tradução ROTA → CHAVE de acesso (rodada PERMISSOES-ACESSO, migration 419).
 *
 * ISTO NÃO É REGRA DE ACESSO. É só um dicionário: "a tela que mora nesta URL
 * se chama assim". Quem pode abrir cada chave é decidido no banco
 * (`acesso_regra`) pela tela de Permissões — perfil, departamento e exceção por
 * pessoa. Se você quer liberar uma tela para alguém, NÃO mexa aqui: mexa em
 * Configurações › Pessoas › Permissões.
 *
 * Antes desta rodada o AdminLayout decidia acesso com duas listas de prefixos
 * de rota (`COMERCIAL_COLAB_ROUTE_PREFIXES` / `COMERCIAL_ORCAMENTISTA_...`).
 * Elas foram removidas: liberar uma pessoa exigia deploy.
 *
 * Rota sem chave aqui = continua ADMIN-ONLY. É de propósito: nada abre por
 * acidente só porque esqueceram de mapear.
 */

export interface RotaGovernada {
  /** Prefixo da URL (sem barra no fim). */
  rota: string;
  /** Chave do catálogo `acesso_tela`. */
  chave: string;
  /**
   * `true` = casa só com a URL exata (sem sub-rotas). Usado no index
   * `/admin/comercial`, que é um redirect pro Funil — sem isso ele engoliria
   * `/admin/comercial/crm`, que é configuração e segue admin-only.
   */
  exata?: boolean;
}

export const ROTAS_GOVERNADAS: ReadonlyArray<RotaGovernada> = [
  // Comercial (CRM v2)
  { rota: '/admin/comercial', chave: 'comercial.funil.ver', exata: true },
  { rota: '/admin/comercial/funil', chave: 'comercial.funil.ver' },
  { rota: '/admin/comercial/leads', chave: 'comercial.leads.ver' },
  { rota: '/admin/comercial/orcamentos', chave: 'comercial.orcamentos.ver' },
  { rota: '/admin/comercial/indicadores', chave: 'comercial.indicadores.ver' },
  { rota: '/admin/comercial/metas', chave: 'comercial.metas.ver' },
  // Financeiro — ver (leitura) × agir (aprovar/simular) são chaves diferentes:
  // é o que permite dar o painel a alguém sem dar as aprovações.
  { rota: '/admin/financeiro/dashboard', chave: 'financeiro.ver' },
  { rota: '/admin/financeiro/simulador', chave: 'financeiro.agir' },
  // Clientes (18/08, incidente do Enzo): o SERVIDOR já exigia `clientes.ver`
  // (mig 425, com regras por departamento/perfil), mas a rota nunca entrou
  // aqui — então o front barrava TODO não-admin com "Acesso restrito",
  // ignorando a tela de Permissões. Permissão atribuída que não surte efeito
  // parece erro de quem configurou; a lacuna era desta linha. Cobre a lista
  // e a ficha (/admin/clientes-v2/:id, casamento por prefixo).
  { rota: '/admin/clientes-v2', chave: 'clientes.ver' },
  /**
   * Comunicados (04/09/2026). ⚠️ **Esta linha é a metade que se esquece.**
   * O servidor já exige `comunicados.ver` (mig 542); sem a rota aqui, o front
   * barraria TODO não-admin com "Acesso restrito" e a tela de Permissões não
   * surtiria efeito — exatamente o que aconteceu com Clientes no incidente do
   * Enzo, em 18/08.
   */
  { rota: '/comunicados', chave: 'comunicados.ver' },
  /**
   * Indicadores da Manutenção (mig 547). ⚠️ Mesma armadilha da linha acima: sem
   * ela, a tela nova abriria só para admin — e a tela de Permissões não teria
   * efeito nenhum sobre ela.
   */
  { rota: '/menu/manutencao/indicadores', chave: 'manutencao.indicadores.ver' },
  // OS do sistema (mig 772): a chave nasce só do admin; no piloto, a tela de Permissões libera.
  { rota: '/menu/manutencao/agenda', chave: 'manutencao.os.ver' },
  { rota: '/menu/manutencao/chamados-os', chave: 'manutencao.os.ver' },
  { rota: '/menu/manutencao/relatorios-os', chave: 'manutencao.os.ver' },
  // D72 (mig 789): planejar as preventivas é de quem cria e agenda OS (o servidor cobra a mesma chave).
  { rota: '/menu/manutencao/planejar-preventivas', chave: 'manutencao.os.gerir' },
  // Achado 12 (28/10): o Planejador é o mesmo planejamento, agora no menu; o endereço antigo leva a ele.
  { rota: '/menu/manutencao/planejador', chave: 'manutencao.os.gerir' },
  /**
   * Projetos (08/10/2026, mig 705). 🔴 A mesma armadilha do Enzo: sem esta linha
   * `/admin/projetos` era admin-only — o Oscar, do departamento Projetos, via o
   * item no menu e caía em "Acesso restrito", em uso real, no levantamento do
   * Fontanário. Cobre a lista e a ficha (prefixo). `/admin/projeto-tipos` é
   * configuração e NÃO entra (segue admin-only). Apagar a obra é outra chave
   * (`projetos.apagar`), cobrada no servidor.
   */
  { rota: '/admin/projetos', chave: 'projetos.ver' },
  /**
   * RH (RH-MENU, 03/10/2026; pedida pelo handoff, R1). ⚠️ Medido ao escrever:
   * esta lista só é lida pelo `AdminLayout` (rotas `/admin/*`) e pelo filtro de
   * departamentos da `Sidebar` — e `/rh` não é nenhum dos dois. Então a linha
   * NÃO barra nem libera nada hoje; e é bom que não barre: `rh.cuidar` é só UMA
   * das três portas do RH (as outras são a diretoria e ser líder). Quem decide
   * o menu é `podeAbrirRh` (servidor, `/auth/me`); quem barra é `/api/v1/rh`.
   */
  { rota: '/rh', chave: 'rh.cuidar' },
];

/**
 * Qual chave governa esta URL? `null` = nenhuma (a tela segue admin-only).
 *
 * Casamento por prefixo MAIS LONGO: `/admin/comercial/orcamentos/abc` cai em
 * `comercial.orcamentos.ver`, não no index do Comercial.
 */
export function chaveDaRota(pathname: string): string | null {
  const limpo = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  let melhor: RotaGovernada | null = null;
  for (const item of ROTAS_GOVERNADAS) {
    const bate = item.exata
      ? limpo === item.rota
      : limpo === item.rota || limpo.startsWith(`${item.rota}/`);
    if (!bate) continue;
    if (!melhor || item.rota.length > melhor.rota.length) melhor = item;
  }
  return melhor ? melhor.chave : null;
}
